import {useCallback, useEffect, useRef, useState} from 'react';
import {SelectInstance} from 'react-select';
import {ISelectOption} from '..';
import {disableScroll, enableScroll, isBrowserContext, isMobileOrTabletByScreen} from 'core/helpers';

interface IProps<T> {
    defaultOption?: ISelectOption<T> | ISelectOption<T>[];
    onClear?: () => void;
    onApply?: () => void;
    isFilterSelect?: boolean;
    onChange?: (value: ISelectOption<T>) => void;
    isMenuOpen?: boolean;
}

export const useInputSelectLogic = <T>({
    defaultOption,
    onClear,
    onApply,
    isFilterSelect,
    onChange,
    isMenuOpen,
}: IProps<T>) => {
    const selectRef = useRef<SelectInstance<unknown, boolean>>(null);
    const [isFilled, setIsFilled] = useState<boolean>(Array.isArray(defaultOption) && Boolean(defaultOption?.length));
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [showOverlay, setShowOverlay] = useState<boolean>(isFilterSelect || false);
    // const [minWidth, setMinWidth] = useState<number>(260);
    const shouldBlockScroll = isMenuOpen || showOverlay;

    const handleSelectChange = useCallback(
        (newValue: ISelectOption<T>) => {
            setIsFilled(Boolean(newValue));
            onChange?.(newValue);
        },
        [onChange],
    );

    const handleSelectOnFocus = useCallback(() => {
        setIsFocused(true);
    }, []);

    const handleSelectOnBlur = useCallback(() => {
        setIsFocused(false);
    }, []);

    const handleClearValue = useCallback(() => {
        onClear?.();
        selectRef.current?.clearValue();
    }, [onClear]);

    const handleApply = useCallback(() => {
        onApply?.();
        selectRef.current?.blur();
    }, [onApply]);

    const handleOverlayClick = useCallback(() => {
        setShowOverlay(false);
    }, []);

    const isTabletOrMobile = isMobileOrTabletByScreen();

    useEffect(() => {
        if (!isBrowserContext()) {
            return;
        }

        if (shouldBlockScroll && isTabletOrMobile) {
            disableScroll();
        } else {
            enableScroll();
        }

        // eslint-disable-next-line consistent-return
        return () => {
            enableScroll();
        };
    }, [isTabletOrMobile, shouldBlockScroll]);

    return {
        selectRef,
        isFilled,
        isFocused,
        showOverlay,
        // minWidth,
        handleSelectChange,
        handleSelectOnFocus,
        handleSelectOnBlur,
        handleClearValue,
        handleApply,
        handleOverlayClick,
        // setMinWidth,
    };
};
